import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, startTransition } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

// Provide a default context value to prevent React error #419 during SSR/hydration
const defaultThemeContext: ThemeContextType = {
  theme: 'light',
  toggleTheme: () => {},
};

const ThemeContext = createContext<ThemeContextType>(defaultThemeContext);

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  return context;
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // HYDRATION FIX: Always initialize to 'light' to match SSR
  // This prevents React Error #418 (hydration mismatch)
  const [theme, setTheme] = useState<Theme>('light');
  const [isHydrated, setIsHydrated] = useState(false);

  // First useEffect: Sync theme state with DOM after hydration
  useEffect(() => {
    // This runs after hydration completes
    // HYDRATION FIX: Wrap in startTransition to prevent React Error #421
    // State updates during hydration without startTransition cause errors when Suspense boundaries are still hydrating
    startTransition(() => {
      setIsHydrated(true);

      // Read the actual theme from DOM (set by index.html inline script)
      if (document.documentElement.classList.contains('dark')) {
        setTheme('dark');
      }
    });
  }, []);

  // Second useEffect: Listen for system preference changes
  useEffect(() => {
    // Only run on client side after hydration
    if (typeof window === 'undefined' || !isHydrated) return;

    // Listen for changes in system preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      startTransition(() => {
        setTheme(e.matches ? 'dark' : 'light');
      });
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isHydrated]);

  // Third useEffect: Update document class when theme changes (only after hydration)
  useEffect(() => {
    // Only run on client side after hydration
    if (typeof window === 'undefined' || !isHydrated) return;

    // Update document based on theme (only when user manually toggles or system preference changes)
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme, isHydrated]);

  // Memoize toggleTheme to prevent recreating on every render
  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => (prevTheme === 'light' ? 'dark' : 'light'));
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(() => ({ theme, toggleTheme }), [theme, toggleTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};